/**
 * Two-Factor Authentication Service
 * 
 * Service สำหรับจัดการ 2FA (TOTP)
 */

import { callCloudFunction } from '@/services/adapters/parse/parse-client'

// ============================================
// Types
// ============================================

export interface GenerateQRCodeResult {
  success: boolean
  secret?: string
  qrCode?: string
  manualEntryKey?: string
  error?: string
}

export interface Verify2FAResult {
  verified: boolean
  error?: string
}

export interface Check2FARequirementResult {
  requires2FA: boolean
  isEnabled: boolean
  enable2FA: boolean
  require2FAForAdmin: boolean
  isAdmin: boolean
  error?: string
}

// ============================================
// Service
// ============================================

export const TwoFactorAuthService = {
  /**
   * สร้าง QR Code สำหรับ 2FA
   */
  async generateQRCode(sessionToken: string, userId: string): Promise<GenerateQRCodeResult> {
    try {
      console.log('[2FA] Calling generate2FAQRCode Cloud Function...', { userId, hasSessionToken: !!sessionToken })
      const result = await callCloudFunction(
        'generate2FAQRCode',
        { userId },
        sessionToken
      )
      console.log('[2FA] Cloud Function response:', { 
        hasSecret: !!result.secret, 
        hasQRCode: !!result.qrCode, 
        hasManualEntryKey: !!result.manualEntryKey,
        resultKeys: Object.keys(result || {})
      })
      if (!result || (!result.secret && !result.qrCode)) {
        console.error('[2FA] Invalid response from Cloud Function:', result)
        return {
          success: false,
          error: 'Invalid response from server. Please check if speakeasy and qrcode are installed on the server.',
        }
      }
      return {
        success: true,
        secret: result.secret,
        qrCode: result.qrCode,
        manualEntryKey: result.manualEntryKey || result.secret,
      }
    } catch (error: any) {
      console.error('[2FA] Generate QR Code error:', error)
      console.error('[2FA] Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack,
      })
      return {
        success: false,
        error: error.message || 'Failed to generate QR code. Please check server logs.',
      }
    }
  },

  /**
   * ตรวจสอบ TOTP code และเปิดใช้งาน 2FA
   */
  async verifyAndEnable(sessionToken: string, userId: string, token: string): Promise<{ success: boolean; error?: string }> {
    try {
      await callCloudFunction(
        'verifyAndEnable2FA',
        { userId, token },
        sessionToken
      )
      return { success: true }
    } catch (error: any) {
      console.error('[2FA] Verify and enable error:', error)
      return {
        success: false,
        error: error.message || 'Invalid verification code',
      }
    }
  },

  /**
   * ปิดการใช้งาน 2FA
   */
  async disable(sessionToken: string, userId: string, token: string): Promise<{ success: boolean; error?: string }> {
    try {
      await callCloudFunction(
        'disable2FA',
        { userId, token },
        sessionToken
      )
      return { success: true }
    } catch (error: any) {
      console.error('[2FA] Disable error:', error)
      return {
        success: false,
        error: error.message || 'Invalid verification code',
      }
    }
  },

  /**
   * ตรวจสอบ TOTP code สำหรับ login
   */
  async verifyToken(userId: string, token: string): Promise<Verify2FAResult> {
    try {
      const result = await callCloudFunction(
        'verify2FAToken',
        { userId, token },
        undefined // ไม่ต้องใช้ session token สำหรับ verify
      )
      return {
        verified: result.verified || false,
      }
    } catch (error: any) {
      console.error('[2FA] Verify token error:', error)
      return {
        verified: false,
        error: error.message || 'Invalid verification code',
      }
    }
  },

  /**
   * ตรวจสอบว่า user ต้องใช้ 2FA หรือไม่
   */
  async checkRequirement(userId: string): Promise<Check2FARequirementResult> {
    try {
      const result = await callCloudFunction(
        'check2FARequirement',
        { userId },
        undefined // ไม่ต้องใช้ session token
      )
      return {
        requires2FA: result.requires2FA || false,
        isEnabled: result.isEnabled || false,
        enable2FA: result.enable2FA || false,
        require2FAForAdmin: result.require2FAForAdmin || false,
        isAdmin: result.isAdmin || false,
      }
    } catch (error: any) {
      console.error('[2FA] Check requirement error:', error)
      return {
        requires2FA: false,
        isEnabled: false,
        enable2FA: false,
        require2FAForAdmin: false,
        isAdmin: false,
        error: error.message,
      }
    }
  },
}

